<template>
  <div class="audiences row mb-36">
    <h1 class="page-header" style="margin:0">build your own audience</h1>
    <div class="text-lg">add attributes in the configurations below to build your audience</div>
    <div
      class="text-sm text-error"
      v-if="isErrored"
    >
      an error has occured. please refresh the page or contact support if the issue remains
    </div>

    <div class="flex flex-col md:flex-row gap-5 mt-5 md:mt-10 audience-container">
      <div style="min-width: 20rem" class="audience-preview-container sm:mb-5">
        <audience-preview v-if="audience"
          :membersCount="membersCount"
          :name.sync="audience.name"
          :isNameEditable="true"
          :isKpiDataOutdated="isMembersCountOutdated"
          :isKpiDataLoading="isMembersCountLoading"
          class="z-10 bg-white"
        />
      </div>
      <div class="audience-content flex flex-col justify-between min-auto flex-1 sm:pr-3">
        <div class="flex flex-col flex-1">

          <template v-if="eventsGroups && audience">
            <transition-group name="audience-rules-list" tag="div">
              <audience-rule v-for="audienceRule in audienceRulesNotDisabled"
                             :key="audienceRule.unique_id"
                             :eventsGroups="eventsGroups"
                             :rule="audienceRule"
                             :rules="audienceRulesNotDisabled"
                             class="audience-rule mb-5"
                             @deleteRule="showDeleteAudienceRuleModal"
              />
            </transition-group>
          </template>
          <div v-else class="flex h-40 items-center justify-center">
            <loading-icon />
          </div>
        </div>

        <div class="buttons-container flex flex-col m-auto mt-16" style="max-width:330px">
          <button v-if="this.audience && this.audience.audience_rules.length < this.audience.rules_limit"
            class="sb-button invert flex-1 rounded-2xl mb-5 border border-blue"
            @click="addRule"
          >
            + add attribute to audience
          </button>
          <button :disabled="!isAudienceValid"
            class="sb-button button flex-1"
            @click="onSubmit"
          >
            save audience
          </button>
        </div>
      </div>
    </div>

    <simple-modal
      v-if="audienceRuleToDelete !== null"
      class="confirm-delete-modal"
      resolveText="remove attribute"
      @rejected="audienceRuleToDelete = null"
      @resolved="deleteRule"
    >
      are you sure you want to remove<br /> this attribute ?
    </simple-modal>

  </div>
</template>

<script>
import AudiencePreview from "./AudiencePreview";
import { LoadingIcon, SimpleModal } from '@springbig/sb-components';
import { audiencesAPI } from "../../services/api/audiencesAPI";
import AudienceRule from "./AudienceRule";
import { debounce, cloneDeep, isEqual } from "lodash";
import dayjs from "dayjs";

export default {
  name: "AudienceEdit",
  components: {AudienceRule, AudiencePreview, LoadingIcon, SimpleModal},
  async created() {
    await this.$store.dispatch('LOAD_CURRENT_USER');
    try {
      this.eventsGroups = (await audiencesAPI.fetchOptions())?.conditions;
      const audienceId = this.$route.params.id;
      let audience;
      if(audienceId) {
        audience = await audiencesAPI.fetchAudience(audienceId);
      }else{
        audience = {
          name: "New audience",
          rules_limit: 5,
          audience_rules: []
        }
      }
      this.audience = this.prepareAudienceForUI(audience);
      this.initialData = cloneDeep(this.audience);
    } catch (error) {
      this.isErrored = true;
    }
    if (!this.audience.audience_rules.length) this.addRule();
  },
  mounted() {
    window.addEventListener('scroll', this.updateAudiencePreviewPosition);
    window.addEventListener('touchmove', this.updateAudiencePreviewPosition);
    window.addEventListener('resize', this.updateAudiencePreviewPosition);
  },
  destroyed() {
    window.removeEventListener('scroll', this.updateAudiencePreviewPosition);
    window.removeEventListener('touchmove', this.updateAudiencePreviewPosition);
    window.removeEventListener('resize', this.updateAudiencePreviewPosition);
  },
  data() {
    return {
      audienceRuleToDelete: null,
      eventsGroups: null,
      audience: null,
      isAudienceValid: false,
      membersCount: 0,
      isMembersCountOutdated: false,
      isMembersCountLoading: false,
      initialData: null,
      debouncedFetchKpiData: debounce(async () => {
        this.isMembersCountLoading = true;
        try {
          this.membersCount = (await audiencesAPI.fetchEstimatedKpiData(this.prepareAudienceForAPI(this.audience)))?.member_count;
          this.isMembersCountOutdated = false;
        } catch (error) {
          this.isErrored = true;
        }
        this.isMembersCountLoading = false;
      }, 500),
      isErrored: false,
    };
  },
  computed: {
    audienceRulesNotDisabled() {
      const audienceRulesNotDisabled = this.audience?.audience_rules.filter(rule => !rule._destroy);
      return _.sortBy(audienceRulesNotDisabled, ['operator_type']);
    },
    isAudienceDirty() {
      return !isEqual(this.initialData.audience_rules, this.audience.audience_rules)
    }
  },
  watch: {
    'audience.audience_rules': {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.isMembersCountOutdated = true;
          this.isAudienceValid = this.audience?.audience_rules.length && !this.audience?.audience_rules.some(rule => !rule.is_valid);
          if(this.isAudienceValid) {
            this.debouncedFetchKpiData();
          }
        });
      }
    }
  },
  methods: {
    updateAudiencePreviewPosition() {
      const content = document.querySelector('.audience-content');
      const preview = document.querySelector('.audience-preview');
      const container = document.querySelector('.audience-container');
      const containerLeft = container.getBoundingClientRect().left
      const containerTop = container.getBoundingClientRect().top + window.pageYOffset
      const viewportWidth = window.innerWidth;
      const topMargin = viewportWidth >= 768 ? 30 : 16;
      if(!container || !preview) return;
      if(window.pageYOffset > (containerTop - topMargin)) {
        content.style.marginTop = `${viewportWidth >= 768 ? 0 : preview.clientHeight}px`;
        if(viewportWidth < 768) {
          preview.style.width = 'auto'
          preview.style.inset = '16px 16px 0'
        }else{
          preview.style.removeProperty('inset')
          preview.style.left = `${containerLeft}px`;
          preview.style.removeProperty('width')
        }
        preview.style.position = 'fixed';
        preview.style.top = `${topMargin}px`;
      }else{
        content.style.marginTop = 0
        preview.style.removeProperty('position')
        preview.style.removeProperty('top')
        preview.style.removeProperty('left')
        preview.style.removeProperty('width')
        preview.style.removeProperty('inset')
      }
    },
    showDeleteAudienceRuleModal(audienceRule) {
      this.audienceRuleToDelete = audienceRule;
    },
    addRule() {
      this.audience.audience_rules.push({
        unique_id: _.uniqueId(),
        operator_type: "and",
        inclusive: true,
        primary_condition: {
          event_type: null,
          event_values: {}
        },
        secondary_conditions: []
      });
    },
    async onSubmit() {
      if (!this.isAudienceValid) return;
      const apiMethod = this.audience.id ? audiencesAPI.updateAudience : audiencesAPI.createAudience;
      const apiParams = this.audience.id
        ? [this.audience.id, this.prepareAudienceForAPI(this.audience)]
        : [this.prepareAudienceForAPI(this.audience)];
      await apiMethod(...apiParams)
        .then(() => {
          this.$router.push(`/audiences`)
        })
        .catch(() => {
          this.isErrored = true;
        })
    },
    deleteRule() {
      // If the rule is coming from the database it's disabled, otherwise it's removed
      if (this.audienceRuleToDelete.id !== undefined) {
        this.audienceRuleToDelete._destroy = true;
        this.audienceRuleToDelete.secondary_conditions = this.audienceRuleToDelete.secondary_conditions.map(c => ({
          ...c,
          _destroy: true
        }));
      } else {
        this.audience.audience_rules = this.audience.audience_rules.filter(rule => rule !== this.audienceRuleToDelete);
      }
      this.audienceRuleToDelete = null;
    },
    prepareAudienceForUI(audience) {
      return {
        ...audience,
        audience_rules: audience?.audience_rules.map(rule => ({
          ...rule,
          is_valid: true,
          unique_id: _.uniqueId(),
          _destroy: false,
          primary_condition: {
            ...rule.primary_condition,
            event_values: _.omit({
              ...rule.primary_condition.event_values,
              ...(() => {
                const updates = {}
                if(rule.primary_condition.event_type === "achieved_event_purchased_within_date_range") {
                  updates.range_min = this.stringToDate(rule.primary_condition.event_values.range_min)
                  updates.range_max = this.stringToDate(rule.primary_condition.event_values.range_max)
                }else if(rule.primary_condition.event_type === "achieved_event_purchased_within_hours_of_the_day") {
                  const date = new Date(0);
                  date.setHours(rule.primary_condition.event_values.range_min,0,0,0)
                  updates.range_min = new Date(date)
                  date.setHours(rule.primary_condition.event_values.range_max,0,0,0)
                  updates.range_max = new Date(date)
                }
                return updates
              })()
            }, ['valid']),
          },
          secondary_conditions: rule.secondary_conditions.map(condition => {
            let updates = {};
            if (['between_date', 'date_range'].includes(condition.event_values?.variant)) {
              updates.range_min = new Date(condition.event_values?.range_min);
              updates.range_max = new Date(condition.event_values?.range_max);
            } else if (['less_than_date', 'greater_than_date'].includes(condition.event_values?.variant)) {
              updates.value = new Date(condition.event_values?.value);
            }
            return {...condition, event_values: {..._.omit(condition.event_values, ['valid']), ...updates}, _destroy: false};
          })
        }))
      };
    },
    prepareEventValues(eventValues, dataType, inputType) {
      const updates = {}
      if(inputType === "12hr_picker_range") {
        if(eventValues.range_min != null) updates.range_min = parseInt(dayjs(eventValues.range_min).format('H'))
        if(eventValues.range_max != null) updates.range_max = parseInt(dayjs(eventValues.range_max).format('H'))
      } else if(dataType === "date") {
        if(eventValues.range_min != null) updates.range_min = dayjs(eventValues.range_min).format('YYYY-MM-DD')
        if(eventValues.range_max != null) updates.range_max = dayjs(eventValues.range_max).format('YYYY-MM-DD')
        if(eventValues.value != null) updates.value = dayjs(eventValues.value).format('YYYY-MM-DD')
      }
      return updates
    },
    prepareAudienceForAPI(audience) {
      return {
        id: audience.id,
        name: audience.name,
        refresh: this.isAudienceDirty,
        audience_rules_attributes: audience.audience_rules.map(rule => {
          const options = this.eventsGroups
                              .reduce((options, currentOptions) => [...options, ...currentOptions.options], [])
                              .find(event => event.key === rule.primary_condition.event_type);
          const primaryCondition = {
            ...rule.primary_condition,
            event_values: {
              ...rule.primary_condition.event_values,
              ...this.prepareEventValues(rule.primary_condition.event_values, options.data_type ? options.data_type : options.options.find(o => o.key === rule.primary_condition.event_values.variant).data_type, options.input_type)
            },
          }
          let secondaryConditions = [
            ...rule.secondary_conditions
                   .filter(condition => !!condition._destroy),
            ...rule.secondary_conditions
                   .filter(condition => !condition._destroy)
                   .map(condition => {
                     const secondaryOptions = options.secondary_conditions
                                                     .find( c => c.key === condition.event_type ).options
                                                     .find( o => o.key === condition.event_values.variant || o.key === condition.event_values.value )
                     return {...condition, event_values: {
                         ...condition.event_values,
                         ...this.prepareEventValues(condition.event_values, secondaryOptions.data_type, secondaryOptions.input_type)
                     }};
                   })
          ]
          return {
            ..._.omit(rule, ['unique_id', 'is_valid', 'primary_condition', 'secondary_conditions']),
            primary_condition_attributes: primaryCondition,
            secondary_conditions_attributes: secondaryConditions
          }
        })
      };
    },
    stringToDate(dateStr) {
      const date = new Date(dateStr);
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()); 
    }
  }
};
</script>

<style lang="scss" scoped>
.temp-dropdown {
  border: 1px solid #128ff9;
  border-radius: 8px;
  color: #128ff9;
  padding: 5px 10px;
  margin-right: 10px;
  font-size: 1.05rem;
}

.audience-rule::v-deep main.sb-dropdown,
main.sb-dropdown {
  .sb-label-container > div > span {
    line-height: 20px;
    height: 20px;
  }
  .sb-rows-container {
    padding-bottom: 0.25rem;
  }
}

.confirm-delete-modal::v-deep {
  > div {
    border-radius: .75rem;
  }
  header {
    font-family: AileronLight, sans-serif;
  }
  section.content {
    font-size: 1.6rem;
    text-align: center;
    padding: 0 40px;
    min-width: unset;
  }
  footer {
    flex-direction: column;
    align-items: center;
    > .sb-button {
      font-size: 1.5rem;
      padding: 1rem 3rem;
    }
    > .sb-reject {
      color: #128ff9;
      background-color: white;
    }
  }
  .close {
    font-size: initial;
    opacity: 0.5
  }
}

.audience-rules-list-move {
  transition: transform .3s;
}

.audience-rules-list-enter-active {
  transition: all .3s;
}

.audience-rules-list-enter {
  opacity: 0;
  transform: translateY(-30px);
}

.buttons-container .sb-button {
  border-radius: 1rem;
}
.audience-preview::v-deep {
  height: fit-content;
  @media only screen and (max-width: 767px) {
    width: 100%;
    box-shadow: 0 0 15px 20px white;
    z-index: 11;
    > label {
      display: inline-block;
    }
    > input {
      display: inline-block;
      width: 75%;
      margin-left: 10px;
    }
    .circle {
      height: 75px;
      &:before {
        display: none;
      }
    }
  }
}
</style>
